const siteObject = {
	name: 'Abstract Store',
	shortname: 'Abstract Store',
	href: '/',
	appID: import.meta.env.VITE_ABSTRACTSTORE_APPID || '',
	baseURL: import.meta.env.VITE_ABSTRACTSTORE_BASE_URL || window.location.origin,
	idBaseURL: import.meta.env.VITE_ABSTRACTSTORE_ID_BASE_URL || 'https://id.abstract3d.com',
	accessRole: import.meta.env.VITE_ABSTRACTSTORE_ACCESS_ROLE || '',
	adminRole: import.meta.env.VITE_ABSTRACTSTORE_ADMIN_ROLE || 'ABSTRACTSTORE_ADMIN',
	backend: {
		baseURL: import.meta.env.VITE_ABSTRACTSTORE_BACKEND_URL || '',
		idBaseURL: import.meta.env.VITE_ABSTRACTSTORE_ID_BACKEND_URL || 'https://api.id.abstract3d.com',
		api: {
			configuration: '/api/configuration',
			userMe: '/api/user/me',
			userList: '/api/user/list',
			user: '/api/user',
			auditList: '/api/audit/list',

			// Storefront catalog (Design §13). `publicAuth` — reachable with no token, which is
			// why these go through `optionalAuthFetch` rather than `authenticatedFetch`.
			catalogFamilies: '/api/catalog/families',
			catalogProducts: '/api/catalog/products',
			catalogProduct: '/api/catalog/product',
			catalogCouponValidate: '/api/catalog/coupon/validate',

			// Checkout (Design §6.2, §14). Both are `requireAuth` — creating a session needs a
			// customer to attach it to, and reading one performs the server's inline reconcile,
			// which is why polling the GET is what advances a session rather than merely observing
			// it.
			checkoutSession: '/api/checkout/session',

			// Account (Design §9, §13). All `requireAuth`, and every one of them resolves through
			// the signed-in user server-side — the uuid in the path names the object, it does not
			// grant access to it. Lists take `?limit=&offset=` and answer `{ items, total }`.
			accountSummary: '/api/account/summary',
			accountSubscriptions: '/api/account/subscriptions',
			accountSubscription: '/api/account/subscription',
			accountOrders: '/api/account/orders',
			accountInvoices: '/api/account/invoices',
			accountInvoice: '/api/account/invoice',
			// Credit notes (Design §4.6) — the paperwork behind a refund. A list of their own
			// rather than a field on the invoice list: a one-time purchase the provider raised no
			// invoice for is credited against its own charge, so hanging them off invoices would
			// make exactly those unreachable.
			accountCreditNotes: '/api/account/credit-notes',
			accountCreditNote: '/api/account/credit-note',
			// Payment methods carry the **local** uuid, never the provider's id (§13).
			accountPaymentMethods: '/api/account/payment-methods',
			accountBilling: '/api/account/billing',
			// Refunds (Design §9.6). The eligibility read is the confirm step's — the lists already
			// carry the same answer inline, so nothing here fans out per row.
			accountRefund: '/api/account/refund',
			accountRefundEligibility: '/api/account/refund-eligibility',

			// Configuration (Design §4.5, §13). Every one of these is admin-only.
			adminSettings: '/api/admin/settings',
			adminSetupStatus: '/api/admin/setup-status',
			adminPaymentProvider: '/api/admin/payment-provider',
			adminPaymentProviderTypes: '/api/admin/payment-provider/types',
			adminSecretsReEncrypt: '/api/admin/secrets/re-encrypt',
			adminCurrency: '/api/admin/currency',
			adminRefundPolicy: '/api/admin/refund-policy',
			adminTaxRate: '/api/admin/tax-rate',
			adminSeller: '/api/admin/seller',
			adminClickwrap: '/api/admin/clickwrap',
			adminProductOption: '/api/admin/product-option',
			adminInvoice: '/api/admin/invoice',
			adminInvoices: '/api/admin/invoices',

			// Applications (Design §4.3, §13). The registry of who may read entitlement state, with
			// what scope, and under which key.
			adminApplication: '/api/admin/application',
			adminAbstractIDApplications: '/api/admin/abstractid/applications',
			// Delivery health (Design §8.1, §13). The replay hangs off an application rather than
			// off a delivery, because it resends *current state*, not a stored attempt.
			adminWebhookDeliveries: '/api/admin/webhook-deliveries',

			// Catalog authoring (Design §13).
			adminFamily: '/api/admin/family',
			// The order of the lines, and of the tiers inside one (§14). Both take the whole
			// visible order rather than an item and a position.
			adminFamilyReorder: '/api/admin/family/reorder',
			adminProductReorder: '/api/admin/product/reorder',
			adminMedia: '/api/admin/media',
			adminProduct: '/api/admin/product',
			adminPrice: '/api/admin/price',
			adminCoupon: '/api/admin/coupon',
			adminCatalogSync: '/api/admin/catalog/sync',

			// Email (Design §11, §13). The sender, the copy and the queue — every one of them an
			// admin action with an audit trail rather than a deployment.
			adminEmailProvider: '/api/admin/email-provider',
			adminEmailProviderTypes: '/api/admin/email-provider/types',
			adminEmailTemplates: '/api/admin/email-templates',
			adminEmailTemplate: '/api/admin/email-template',
			adminEmailMessages: '/api/admin/email-messages',

			// Operations (Design §13, §16). Nothing here blocks checkout, which is exactly why it
			// needs a screen: the inline reconcile keeps purchases completing with every worker
			// stopped, so these are the only numbers that move when the machinery dies.
			// Storefront analytics (Design §12). The collect endpoint is `publicAuth` and goes
			// through `stores/analytics.ts` rather than `AppNetwork`, because it must be callable
			// from a `sendBeacon` on the way out of the page.
			analyticsCollect: '/api/analytics/collect',
			adminAnalytics: '/api/admin/analytics',
			// Financial reports (Design §13). Beside analytics rather than inside it: analytics
			// reads the visit log and answers "what did people do", this reads invoices, orders and
			// refunds and answers "what did the store take".
			adminReports: '/api/admin/reports',
			// §13. Migrating the legacy store. The **upload** is mounted outside the API router —
			// ahead of the body parser — but it is the same URL space, so it is listed here too.
			adminMigrationBatch: '/api/admin/migration/batch',
			adminMigrationMappingTargets: '/api/admin/migration/mapping-targets',
			adminMigrationLookup: '/api/admin/migration/lookup',
			adminCreditNote: '/api/admin/credit-note',

			adminMetrics: '/api/admin/metrics',
			adminOperationsHealth: '/api/admin/operations/health',
			adminOperationsDeliveries: '/api/admin/operations/deliveries',
			adminProviderEvents: '/api/admin/provider-events',
			adminReconciliation: '/api/admin/reconciliation',
			adminCatalogSyncStatus: '/api/admin/catalog/sync-status',

			// Support and money out (Design §9.6, §13). A customer is addressed by the **user's**
			// uuid, because one customer is one user and it has no identifier of its own.
			adminCustomer: '/api/admin/customer',
			// Store-wide, as opposed to the per-customer lists on a customer's own page.
			adminSubscriptions: '/api/admin/subscriptions',
			adminOrders: '/api/admin/orders',
			// The detail screens behind those two lists.
			adminSubscription: '/api/admin/subscription',
			adminOrder: '/api/admin/order',
			adminRefund: '/api/admin/refund'
		},

		/**
		 * AbstractID's own API, called **cross-origin** and with no Store token involved.
		 *
		 * These are the endpoints behind the passwordless sign-in of Design §14: a customer gives
		 * an email address, AbstractID emails a six-digit code, and verifying it hands back the
		 * very same JWT the `/authorize` redirect would have — signed with the same secret, in the
		 * same shape — which is what lets the Store accept it with no server change. AbstractID
		 * **creates the account itself** on the first request, so a first-time buyer never fills in
		 * a registration form and never leaves the storefront.
		 *
		 * They hang off `backend.idBaseURL` rather than `backend.baseURL`: the identity provider is
		 * a separate host in every environment, and `/user-backend/…` beside them already is.
		 */
		id: {
			/** POST `{ email, force }` → `{ method: 'email' | 'password' | 'passkey' }`. */
			emailOTPRequest: '/api/auth/email-otp/request',
			/** POST `{ email, code }` → `{ token, requiresTwoFactor, twoFactorToken }`. */
			emailOTPVerify: '/api/auth/email-otp/verify',
			/** POST `{ twoFactorToken, code }` → `{ token }`, for an account with TOTP enabled. */
			twoFactorVerify: '/api/auth/2fa/verify',
			/**
			 * POST `{ appUUID, returnURL }`, bearing the freshly issued token.
			 *
			 * The redirect flow's consent step, which the inline flow would otherwise skip: it is
			 * what registers the customer against this application at the identity provider. Skip
			 * it and a customer who only ever bought through the OTP flow is absent from the
			 * Store's own entry in AbstractID.
			 */
			authorize: '/api/auth/authorize'
		}
	}
};

export default siteObject;
